import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

const ROOT = ['kit-orders'] as const;

export type OrderCategory = 'Uniform' | 'PPE';

export interface OrderLine {
  item: string;
  category: OrderCategory;
  size: string;
  needed: number;
  ordered: number;
  outstanding: number;
  people_total: number;
  people_outstanding: number;
  last_ordered_at: string | null;
  new_since_last_order: number;
}

export interface PersonOrderItem {
  item: string;
  category: OrderCategory;
  size: string;
  quantity: number;
  ordered: boolean;
  ordered_at: string | null;
}

export interface PersonOrderRow {
  person_id: string;
  person_name: string;
  items: PersonOrderItem[];
  any_outstanding: boolean;
}

export interface KitOrderBoard {
  lines: OrderLine[];
  people: PersonOrderRow[];
}

/** One demand unit to mark/unmark — a person's units of an item at a size. */
export interface OrderUnit {
  person_id: string;
  item_name: string;
  size: string;
}

export function useKitOrders() {
  return useQuery({
    queryKey: ROOT,
    queryFn: () => api<KitOrderBoard>('/api/admin/kit-orders'),
  });
}

function useBoardMutation(path: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (units: OrderUnit[]) =>
      api<KitOrderBoard>(`/api/admin/kit-orders/${path}`, {
        method: 'POST',
        body: JSON.stringify({ units }),
      }),
    onSuccess: (board) => {
      // The endpoints return the fresh board, so seed the cache directly.
      qc.setQueryData(ROOT, board);
    },
  });
}

export function useMarkOrdered() {
  return useBoardMutation('mark');
}

export function useUnmarkOrdered() {
  return useBoardMutation('unmark');
}

/** A person's resolved kit item for one station (sized + gate-aware). */
export interface ResolvedKitItem {
  station: string;
  name: string;
  quantity: number;
  category?: OrderCategory;
  size_required: boolean;
  linked: boolean;
  size?: string;
}

/**
 * A person's resolved kit for a station: the items their role kit entitles them
 * to, sized from their submission, with buy-my-own items already dropped. Used
 * to populate the kit-issue drawer.
 */
export function usePersonStationKit(personId: string | null, station: string) {
  return useQuery({
    queryKey: ['station-kit', personId, station] as const,
    enabled: !!personId,
    queryFn: () =>
      api<{ items: ResolvedKitItem[] }>(
        `/api/people/${personId}/station-kit?station=${station}`,
      ).then((r) => r.items),
  });
}
